import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  NotificationsActive as NotificationsActiveIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import DetalleEcf, {
  ESTADOS_ECF,
  ChipEstadoEcf,
  formatMoneyEcf,
  formatFechaHoraEcf,
  labelTipoEcf,
  labelEstadoEcf,
  textoRazonesEcf,
} from 'Fiscal/DetalleEcf';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

/** Clave del programa para validar permisos (misma regla que FacturaPOS). */
export const PROGRAMA_DASHBOARD_NOTIFICACIONES = 'dashboardnotificaciones';

const TIPOS_ECF = [
  { value: '', label: 'Todos' },
  { value: '31', label: 'E31 — Crédito fiscal' },
  { value: '32', label: 'E32 — Consumo' },
  { value: '33', label: 'E33 — Nota de débito' },
  { value: '34', label: 'E34 — Nota de crédito' },
  { value: '43', label: 'E43 — Gastos menores' },
  { value: '44', label: 'E44 — Régimen especial' },
  { value: '45', label: 'E45 — Gubernamental' },
];

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const anchosColumnas = ['13%', '6%', '11%', '12%', '9%', '8%', '10%', '8%', '10%', '8%', '5%'];

const fechaHoyIso = () => new Date().toISOString().slice(0, 10);
const primerDiaMesIso = () => {
  const hoy = fechaHoyIso();
  return `${hoy.slice(0, 8)}01`;
};

const DashboardNotificaciones = () => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, nombreNegocio, rnc_empresa } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_DASHBOARD_NOTIFICACIONES),
    [roleDetalles]
  );

  const [emitidos, setEmitidos] = useState([]);
  const [ambiente, setAmbiente] = useState('');
  const [cargando, setCargando] = useState(false);
  const [refrescandoEncf, setRefrescandoEncf] = useState('');
  const [reintentando, setReintentando] = useState(false);

  const [fechaDesde, setFechaDesde] = useState(primerDiaMesIso);
  const [fechaHasta, setFechaHasta] = useState(fechaHoyIso);
  const [estado, setEstado] = useState('');
  const [tipoEcf, setTipoEcf] = useState('');
  const [encf, setEncf] = useState('');

  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [filaDetalle, setFilaDetalle] = useState(null);

  const credenciales = useCallback(
    () => ({
      usuario: usuarioLog || '',
      password: claveLog || '', empresa: empresaFiscal,
    }),
    [usuarioLog, claveLog]
  );

  const cargarEmitidos = useCallback(async () => {
    if (!puedeLeer || !usuarioLog) return;
    if (fechaDesde && fechaHasta && fechaDesde > fechaHasta) {
      showError('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/fiscal_listarecfemitidos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          fecha_desde: fechaDesde || undefined,
          fecha_hasta: fechaHasta || undefined,
          estado: estado || undefined,
          tipo_ecf: tipoEcf || undefined,
          encf: encf.trim() || undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setEmitidos(data.emitidos || []);
        setAmbiente(data.ambiente || '');
      } else {
        setEmitidos([]);
        showError(data.Error || 'Error al cargar las notificaciones fiscales');
      }
    } catch {
      setEmitidos([]);
      showError('Error de conexión al cargar las notificaciones fiscales');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    usuarioLog,
    servidor,
    fechaDesde,
    fechaHasta,
    estado,
    tipoEcf,
    encf,
    credenciales,
    showError,
  ]);

  useEffect(() => {
    cargarEmitidos();
  }, [puedeLeer, usuarioLog]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLimpiarFiltros = () => {
    setFechaDesde(primerDiaMesIso());
    setFechaHasta(fechaHoyIso());
    setEstado('');
    setTipoEcf('');
    setEncf('');
  };

  const abrirDetalle = (fila) => {
    setFilaDetalle(fila);
    setDetalleAbierto(true);
  };

  const refrescarEstado = async (fila) => {
    if (!puedeLeer || !usuarioLog) return;
    const encfFila = fila?.encf;
    if (!encfFila) return;
    if (!fila?.track_id) {
      showError('Este comprobante no tiene TrackId para consultar a la DGII');
      return;
    }
    setRefrescandoEncf(encfFila);
    try {
      const response = await fetch(servidor + '/api/fiscal_estadoecf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...credenciales(), encf: encfFila }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setEmitidos((prev) =>
          prev.map((item) =>
            item.encf === encfFila
              ? {
                  ...item,
                  estado: data.estado || item.estado,
                  respuesta: data.respuesta || item.respuesta,
                  razones: data.razones || item.razones,
                  track_id: data.track_id || item.track_id,
                  codigo_seguridad: data.codigo_seguridad || item.codigo_seguridad,
                  fecha_firma: data.fecha_firma || item.fecha_firma,
                }
              : item
          )
        );
        setFilaDetalle((prev) =>
          prev && prev.encf === encfFila
            ? {
                ...prev,
                estado: data.estado || prev.estado,
                respuesta: data.respuesta || prev.respuesta,
                razones: data.razones || prev.razones,
                track_id: data.track_id || prev.track_id,
              }
            : prev
        );
        showSuccess(`Estado de ${encfFila}: ${labelEstadoEcf(data.estado)}`);
      } else {
        showError(data.Error || 'No se pudo refrescar el estado ante la DGII');
      }
    } catch {
      showError('Error de conexión al refrescar el estado');
    } finally {
      setRefrescandoEncf('');
    }
  };

  const reintentarPendientes = async () => {
    if (!puedeLeer || !usuarioLog) return;
    setReintentando(true);
    try {
      const response = await fetch(servidor + '/api/fiscal_reintentarenvios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          rncemisor: emisorFiscal.rnc || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const cantidad = (data.procesados || []).length;
        showSuccess(
          cantidad
            ? `Se procesaron ${cantidad} comprobante(s) pendiente(s)`
            : 'No había comprobantes pendientes de reenvío'
        );
        await cargarEmitidos();
      } else {
        showError(data.Error || data.error || 'No se pudieron reintentar los envíos');
      }
    } catch {
      showError('Error de conexión al reintentar envíos');
    } finally {
      setReintentando(false);
    }
  };

  const resumen = useMemo(() => {
    const counts = {
      total: emitidos.length,
      aceptados: 0,
      rechazados: 0,
      pendientes: 0,
    };
    emitidos.forEach((fila) => {
      const key = String(fila.estado || '').toLowerCase();
      if (key === 'aceptado' || key === 'aceptado_condicional') counts.aceptados += 1;
      else if (key === 'rechazado' || key === 'error') counts.rechazados += 1;
      else counts.pendientes += 1;
    });
    return counts;
  }, [emitidos]);

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader
          title="Dashboard Notificaciones"
          subtitle={emisorFiscal.nombre ? `${emisorFiscal.nombre} · Fiscal` : 'Notificaciones fiscales e-CF'}
        />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <NotificationsActiveIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder al dashboard de notificaciones fiscales.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Dashboard Notificaciones"
        subtitle={emisorFiscal.nombre ? `${emisorFiscal.nombre} · e-CF emitidos` : 'Notificaciones fiscales e-CF'}
        cta={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={reintentando ? <CircularProgress size={16} /> : <ReplayIcon />}
              onClick={reintentarPendientes}
              disabled={reintentando || cargando || !usuarioLog}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
              }}
            >
              Reintentar pendientes
            </Button>
            <Button
              variant="contained"
              startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={cargarEmitidos}
              disabled={cargando || !usuarioLog}
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                px: 2.2,
                height: 38,
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Actualizar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Comprobantes"
              value={String(resumen.total)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Aceptados"
              value={String(resumen.aceptados)}
              icon={CheckCircleIcon}
              accent={FP.color.successDark}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Rechazados"
              value={String(resumen.rechazados)}
              icon={CancelIcon}
              accent={FP.color.danger}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Pendientes"
              value={String(resumen.pendientes)}
              icon={HourglassEmptyIcon}
              accent={FP.color.warning}
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <NotificationsActiveIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            {ambiente ? (
              <Chip
                size="small"
                label={`Ambiente ${ambiente}`}
                sx={{
                  ml: 0.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                }}
              />
            ) : null}
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Estado DGII"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {ESTADOS_ECF.map((op) => (
                    <MenuItem key={op.value || 'todos'} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Tipo e-CF"
                  value={tipoEcf}
                  onChange={(e) => setTipoEcf(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {TIPOS_ECF.map((op) => (
                    <MenuItem key={op.value || 'todos'} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="eNCF"
                  value={encf}
                  onChange={(e) => setEncf(e.target.value.toUpperCase())}
                  placeholder="E32000…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={cargarEmitidos}
                    disabled={cargando || !usuarioLog}
                    sx={{
                      height: 46,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: 'none',
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                    }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.6,
                      textTransform: 'none',
                      fontWeight: 700,
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Comprobantes electrónicos emitidos
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : emitidos.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <ReceiptIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use los filtros y pulse «Buscar» para listar los e-CF emitidos y sus notificaciones.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1280, sm: 1480 } }}>
                  <colgroup>
                    {anchosColumnas.map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>eNCF</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Tipo
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>Documento</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>Cliente</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>RNC</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Monto
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Estado
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Cód. seguridad
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fecha firma
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>Razones</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {emitidos.map((fila) => {
                      const razones = textoRazonesEcf(fila);
                      return (
                        <TableRow key={`${fila.encf}-${fila.id || fila.creado}`} hover>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.82rem',
                              fontWeight: 700,
                              color: FP.color.textPrimary,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={fila.encf || '—'}
                          >
                            {fila.encf || '—'}
                          </TableCell>
                          <TableCell align="center">{labelTipoEcf(fila.tipo_ecf)}</TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={fila.tipofactura || (fila.nfact ? `Factura ${fila.nfact}` : '—')}
                          >
                            {fila.tipofactura || (fila.nfact ? `#${fila.nfact}` : '—')}
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={fila.nombre_cliente || '—'}
                          >
                            {fila.nombre_cliente || '—'}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.82rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={fila.rnc_comprador || '—'}
                          >
                            {fila.rnc_comprador || '—'}
                          </TableCell>
                          <TableCell align="right">
                            ${formatMoneyEcf(fila.monto_total)}
                          </TableCell>
                          <TableCell align="center">
                            <ChipEstadoEcf estado={fila.estado} />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700 }}
                          >
                            {fila.codigo_seguridad || '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.82rem' }}>
                            {formatFechaHoraEcf(fila.fecha_firma)}
                          </TableCell>
                          <TableCell
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: razones ? FP.color.danger : FP.color.textMuted,
                              fontSize: '0.8rem',
                            }}
                            title={razones || 'Sin mensajes'}
                          >
                            {razones || '—'}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.4, justifyContent: 'center' }}>
                              <Tooltip title="Ver detalle">
                                <IconButton
                                  size="small"
                                  onClick={() => abrirDetalle(fila)}
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={fila.track_id ? 'Refrescar estado DGII' : 'Sin TrackId'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!fila.track_id || refrescandoEncf === fila.encf}
                                    onClick={() => refrescarEstado(fila)}
                                    sx={{
                                      color: FP.color.navy,
                                      '&:hover': { bgcolor: FP.color.surfaceAlt },
                                      '&.Mui-disabled': { color: FP.color.textMuted },
                                    }}
                                  >
                                    {refrescandoEncf === fila.encf ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <RefreshIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <DetalleEcf
        open={detalleAbierto}
        onClose={() => setDetalleAbierto(false)}
        emitido={filaDetalle}
        ambiente={ambiente}
        onEstadoActualizado={(actualizado) => {
          const encfActualizado = actualizado?.encf;
          if (!encfActualizado) return;
          setEmitidos((prev) =>
            prev.map((item) => (item.encf === encfActualizado ? { ...item, ...actualizado } : item))
          );
          setFilaDetalle((prev) =>
            prev && prev.encf === encfActualizado ? { ...prev, ...actualizado } : prev
          );
        }}
      />
    </Box>
  );
};

export default DashboardNotificaciones;
